import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { initTracking, track } from '../../shared-web/track';
import { initPageEngagement } from '../../shared-web/page-engagement';
import { syncInternalVisitorMarker } from '../../shared-web/internal-visitor';
import { INSTALL_PROMPT_KEY, initInstallCompletion } from '../../shared-web/install-guide.js';
import { initStaleBundleReload, readAppVersionMeta } from '../../shared-web/stale-bundle-reload';

// Capture `beforeinstallprompt` as EARLY as possible (module eval, before React
// hydrates). Chromium fires it once at load — before AddToHomeButton's own
// listener attaches — so without this the deferred prompt is lost and the
// homepage's install affordance has no native path. Twin of the app bundle's
// capture in src/web/src/main.tsx; both park it under the same key.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as unknown as Record<string, unknown>)[INSTALL_PROMPT_KEY] = e;
});

// …and register the pass-through service worker, because Chromium fires that
// event for nobody without one. The manifest alone is not enough: installability
// requires a registered worker WITH a fetch handler, which is the only reason
// /sw.js exists (it caches nothing — see public/sw.js). Without this the
// homepage's Install button could only ever teach the manual taps, even on
// Android where the one-tap install is real. Same registration the app bundle
// does (src/web/src/main.tsx); one worker, scope '/', serves both.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* non-fatal */ });
  });
}

// Internal-visitor marker, applied BEFORE the first beacon so the very page
// load that carries `?internal=1` is already excluded — otherwise the visit
// that turns the marker on is the one visit it can never drop. No-op without
// the param.
syncInternalVisitorMarker();

/**
 * The referring HOST, or '' when there is none. Host only, on purpose: enough
 * to see where traffic comes from, without carrying whatever a third-party
 * URL happened to have in its path or query.
 */
function referrerHost(): string {
  try {
    const ref = document.referrer || '';
    if (!ref) return '';
    const host = new URL(ref).host;
    return host === window.location.host ? '(internal)' : host;
  } catch {
    return '';
  }
}

// page.rendered — the page's only sign of life that doesn't require a click,
// fired the moment the bundle executes. The server's page.viewed (minted per
// GET) is the denominator: viewed ≈ rendered means the page runs and any loss
// is the content; rendered ≪ viewed means the bundle never ran and the loss
// is the loading. Also installs the delegated ui.clicked listener and the
// page.error handlers.
initTracking({
  // This bundle serves the brand/legal/content pages, never `/` — surface
  // 'marketing' matches the server's page.viewed for the same URLs (the SPA
  // owns 'landing').
  surface: 'marketing',
  // Every URL here is its own document, so the path IS the route and it stays
  // true for the life of the page; nothing re-declares it.
  route: window.location.pathname || '/',
  renderProps: {
    route: window.location.pathname || '/',
    referrer_host: referrerHost(),
  },
});
// install.completed (a standalone launch, Chromium's appinstalled), observed
// at bundle start on any page, not only where the install button is mounted.
initInstallCompletion({ win: window });

// A page running an older bundle than the live deploy reloads itself onto it
// (shared-web stale-bundle-reload; spec §1.6; 2026-10-01 for this bundle). The
// served version is the <meta> the edge stamps into every marketing document
// (serveMarketingHtml). This bundle makes no apiFetch calls, so it hears of a
// deploy from /__version alone — on a return to the foreground and on the site
// clock. Nothing is typed on these pages, so nothing needs keeping.
initStaleBundleReload(readAppVersionMeta());

// page.scrolled — a timestamped milestone at 25/50/75/100% first reached: the
// timeline of how far down they got, delivered while the visit is still live
// (the pagehide summary is the beacon most likely to be lost). Depth truth
// lives on these rows; page.engaged carries no scroll copy.
// page.engaged — one summary at teardown: how long the page held someone and
// how many taps.
initPageEngagement({
  onScrollDepth: (depth) => {
    let t: number | null = null;
    try { t = Math.round(performance.now()); } catch { /* t stays null */ }
    track('page.scrolled', { depth, t_ms: t, route: window.location.pathname || '/' });
  },
  onEngagement: (s) => track('page.engaged', {
    route: window.location.pathname || '/',
    visit_id: s.visit_id,
    dwell_ms: s.dwell_ms,
    visible_ms: s.visible_ms,
    max_scroll_pct: s.max_scroll_pct,
    clicks: s.clicks,
  }),
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
